import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/crm/CrmShell";
import { Btn, Field, inputCls, EmptyState, Modal, fmtDay } from "@/components/crm/kit";
import { Share2, Plus, AlertTriangle, Clock } from "lucide-react";

const REF_TONE = {
  Submitted: "bg-slate-100 text-slate-600", Contacted: "bg-sky-100 text-sky-700",
  Accepted: "bg-indigo-100 text-indigo-700", "In Progress": "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700", Declined: "bg-red-100 text-red-700", Cancelled: "bg-slate-200 text-slate-500",
};
const OPEN = ["Submitted", "Contacted", "Accepted", "In Progress"];
const todayStr = () => new Date().toISOString().slice(0, 10);
const isOverdue = (r) => r.next_followup_date && OPEN.includes(r.status) && String(r.next_followup_date).slice(0, 10) < todayStr();

export default function ReferralTracking() {
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState({ referral_statuses: [], services: [], users: [] });
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ client_id: "", service: "", partner: "", owner_id: "", status: "", date_from: "", date_to: "" });
  const [showNew, setShowNew] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ referring_client_id: "", service: "", receiving_partner: "", owner_id: "", referral_date: "", next_followup_date: "", notes: "" });

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/crm/referrals", { params });
    setRows(r.data);
  }, [filters]);

  useEffect(() => {
    api.get("/crm/hudlup/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/crm/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refId = sp.get("ref");
    if (refId) api.get(`/crm/referrals/${refId}`).then((r) => setSel(r.data)).catch(() => {});
  }, [sp]);

  const create = async () => {
    if (!form.referring_client_id) return toast.error("Choose a referring client");
    try {
      const client = clients.find((c) => c.id === form.referring_client_id);
      await api.post("/crm/referrals", { ...form, referring_client_name: client?.name });
      toast.success("Referral created"); setShowNew(false);
      setForm({ referring_client_id: "", service: "", receiving_partner: "", owner_id: "", referral_date: "", next_followup_date: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const patchSel = async (patch) => {
    try { const r = await api.patch(`/crm/referrals/${sel.id}`, patch); setSel(r.data); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const closeDetail = () => { setSel(null); if (sp.get("ref")) { sp.delete("ref"); setSp(sp, { replace: true }); } };

  const overdueCount = rows.filter(isOverdue).length;

  return (
    <div>
      <PageHeader title="Referral Tracking" subtitle="Follow referrals from submission through completion">
        <Btn onClick={() => setShowNew(true)} data-testid="rt-new"><Plus className="h-4 w-4" /> New Referral</Btn>
      </PageHeader>

      <div className="p-6">
        {overdueCount > 0 && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700" data-testid="rt-overdue-banner">
            <AlertTriangle className="h-4 w-4" /> {overdueCount} referral{overdueCount > 1 ? "s have" : " has"} an overdue follow-up
          </div>
        )}

        {/* Filters */}
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <select data-testid="rt-f-client" className={inputCls} value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}>
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="rt-f-service" className={inputCls} value={filters.service} onChange={(e) => setFilters({ ...filters, service: e.target.value })}>
            <option value="">All services</option>
            {meta.services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <input data-testid="rt-f-partner" className={inputCls} placeholder="Receiving partner…" value={filters.partner} onChange={(e) => setFilters({ ...filters, partner: e.target.value })} />
          <select data-testid="rt-f-owner" className={inputCls} value={filters.owner_id} onChange={(e) => setFilters({ ...filters, owner_id: e.target.value })}>
            <option value="">All owners</option>
            {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select data-testid="rt-f-status" className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All statuses</option>
            {meta.referral_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-1 text-xs text-slate-500">From <input type="date" data-testid="rt-f-from" className={inputCls} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></label>
          <label className="flex items-center gap-1 text-xs text-slate-500">To <input type="date" data-testid="rt-f-to" className={inputCls} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></label>
          <Btn variant="ghost" onClick={() => setFilters({ client_id: "", service: "", partner: "", owner_id: "", status: "", date_from: "", date_to: "" })}>Clear</Btn>
        </div>

        {rows.length === 0 ? (
          <EmptyState icon={Share2} title="No referrals found" hint="Create a referral or adjust your filters." />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Ref #</th><th className="px-4 py-3">Referring client</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Receiving partner</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Follow-up</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`rt-row-${r.id}`} onClick={() => setSel(r)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-400">{r.ref_number}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{r.referring_client_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.service || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.receiving_partner || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.owner_name || "—"}</td>
                    <td className="px-4 py-3 text-xs">{r.next_followup_date ? <span className={isOverdue(r) ? "font-bold text-amber-600" : "text-slate-500"}>{isOverdue(r) && <Clock className="mr-1 inline h-3 w-3" />}{fmtDay(r.next_followup_date)}</span> : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${REF_TONE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New referral */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="New Referral">
        <Field label="Referring client / practice *">
          <select data-testid="rt-new-client" className={inputCls} value={form.referring_client_id} onChange={(e) => setForm({ ...form, referring_client_id: e.target.value })}>
            <option value="">Choose a client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Service">
            <select data-testid="rt-new-service" className={inputCls} value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
              <option value="">—</option>
              {meta.services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Receiving partner"><input data-testid="rt-new-partner" className={inputCls} value={form.receiving_partner} onChange={(e) => setForm({ ...form, receiving_partner: e.target.value })} /></Field>
          <Field label="Owner">
            <select className={inputCls} value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
              <option value="">Me</option>
              {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Referral date"><input type="date" className={inputCls} value={form.referral_date} onChange={(e) => setForm({ ...form, referral_date: e.target.value })} /></Field>
          <Field label="Next follow-up"><input type="date" className={inputCls} value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} /></Field>
        </div>
        <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        <Btn onClick={create} data-testid="rt-new-save" className="mt-2 w-full justify-center">Create Referral</Btn>
      </Modal>

      {/* Detail */}
      <Modal open={!!sel} onClose={closeDetail} title={sel ? `Referral ${sel.ref_number}` : ""} wide>
        {sel && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Info label="Referring client" value={sel.referring_client_name} />
              <Info label="Referral date" value={fmtDay(sel.referral_date)} />
              <Field label="Status">
                <select data-testid="rt-detail-status" className={`${inputCls} font-semibold`} value={sel.status} onChange={(e) => patchSel({ status: e.target.value })}>
                  {meta.referral_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Service">
                <select className={inputCls} value={sel.service || ""} onChange={(e) => patchSel({ service: e.target.value })}>
                  <option value="">—</option>
                  {meta.services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Receiving partner"><input defaultValue={sel.receiving_partner || ""} onBlur={(e) => { if (e.target.value !== (sel.receiving_partner || "")) patchSel({ receiving_partner: e.target.value }); }} className={inputCls} /></Field>
              <Field label="Owner">
                <select className={inputCls} value={sel.owner_id || ""} onChange={(e) => patchSel({ owner_id: e.target.value })}>
                  <option value="">—</option>
                  {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
              <Field label="Next follow-up"><input type="date" defaultValue={sel.next_followup_date ? String(sel.next_followup_date).slice(0, 10) : ""} onBlur={(e) => patchSel({ next_followup_date: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Notes"><textarea defaultValue={sel.notes || ""} onBlur={(e) => { if (e.target.value !== (sel.notes || "")) patchSel({ notes: e.target.value }); }} className={inputCls} rows={2} /></Field>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Status history</p>
              <div className="space-y-2">
                {(sel.status_history || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold">{h.from || "—"} → {h.to}</span>
                    <span>{h.by_name} · {fmtDay(h.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Info({ label, value }) {
  return <div><p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p><p className="text-sm font-semibold text-slate-700">{value || "—"}</p></div>;
}
